import React from 'react';
import { ClaimResponse } from '@sentara-health/sentara-api-hooks-core';
import {
  SortField,
  useClaimsIndex,
  useClaimsQuery,
  useInfiniteScroll,
} from 'common';
import { ClaimsFilterState } from '../types/claimsFilters';
import {
  applyFiltersToQueriedClaims,
  applySortToQueriedClaims,
  hasActiveFilters,
} from '../utils/claimsFilterUtils';

export interface UseClaimsSortFilterResult {
  queriedClaims: ClaimResponse[];
  displayedClaims: ClaimResponse[];
  intersectionObserverTargetRef: React.MutableRefObject<HTMLDivElement | null>;
  hasMoreItemsToLoad: boolean;
}

export interface UseClaimsSortFilterProps {
  claims: ClaimResponse[];
  selectedSortField: SortField | '';
  appliedFilters: ClaimsFilterState;
  pageSize?: number;
}

export const useClaimsSortFilter = ({
  claims,
  selectedSortField,
  appliedFilters,
  pageSize = 10,
}: UseClaimsSortFilterProps): UseClaimsSortFilterResult => {
  const claimsIndex = useClaimsIndex(claims);

  const useClaimsQueryFn =
    typeof useClaimsQuery === 'function' ? useClaimsQuery : () => ({ claims });

  const queryResult = useClaimsQueryFn({
    index: claimsIndex,
    sortBy: (selectedSortField as SortField) || 'dateOfService',
    filters: appliedFilters,
  }) || { claims };

  let queriedClaims: ClaimResponse[] = Array.isArray(queryResult?.claims)
    ? queryResult.claims
    : claims;

  const originalIds = claims.map((c: any) => c?.id ?? '');
  const newIds = queriedClaims.map((c: any) => c?.id ?? '');

  const smartQueryChangedLength = newIds.length !== originalIds.length;
  const smartQueryChangedOrder =
    JSON.stringify(originalIds) !== JSON.stringify(newIds);

  const smartQueryChangedSortOrder = (() => {
    if (!selectedSortField) return false;
    const field = selectedSortField as string;
    const orig = claims.map((c: any) => c?.[field]);
    const now = queriedClaims.map((c: any) => c?.[field]);
    return JSON.stringify(orig) !== JSON.stringify(now);
  })();

  const smartQueryChangedDueToFilters = (() => {
    if (!hasActiveFilters(appliedFilters)) return false;
    return smartQueryChangedLength;
  })();

  const smartQueryMeaningful =
    smartQueryChangedLength ||
    smartQueryChangedOrder ||
    smartQueryChangedSortOrder ||
    smartQueryChangedDueToFilters;

  if (!smartQueryMeaningful) {
    queriedClaims = applyFiltersToQueriedClaims(queriedClaims, appliedFilters);
    queriedClaims = applySortToQueriedClaims(queriedClaims, selectedSortField);
  }

  const useInfiniteScrollFn =
    typeof useInfiniteScroll === 'function'
      ? useInfiniteScroll
      : () => ({
          displayedClaims: queriedClaims,
          intersectionObserverTargetRef: { current: null },
          hasMoreItemsToLoad: false,
        });

  const infiniteScrollResult = useInfiniteScrollFn(
    queriedClaims,
    pageSize,
    selectedSortField,
    appliedFilters
  ) || {
    displayedClaims: queriedClaims,
    intersectionObserverTargetRef: { current: null },
    hasMoreItemsToLoad: false,
  };

  const {
    displayedClaims = queriedClaims,
    intersectionObserverTargetRef = { current: null },
    hasMoreItemsToLoad = false,
  } = infiniteScrollResult;

  return {
    queriedClaims,
    displayedClaims,
    intersectionObserverTargetRef,
    hasMoreItemsToLoad,
  };
};
